import { supabase } from '../supabase/client';
import { PRICING_PLANS, CREDIT_CONFIG, getPlanById } from '../config/stripe';
import { findOrCreateStripeCustomer, cancelStripeSubscription } from './stripeService';
import {
  averageOverallQualityScoreFromStorageRows,
  buildDashboardMetricsForDb,
} from '../lib/analysisMetrics';

// Window (in days) during which a paid subscription can be canceled with a
// full refund. Anchored on user_billing.subscription_started_at.
export const REFUND_WINDOW_DAYS = 3;

export type RedeemPromoCodeResult =
  | { ok: true; creditsGranted: number }
  | { ok: false; code: string; message: string };

const REDEEM_PROMO_MESSAGES: Record<string, string> = {
  invalid_user: 'Please sign in to redeem a code.',
  invalid_code: 'Enter a promo code.',
  no_billing: 'Your account is not ready yet. Refresh the page or contact support.',
  invalid_or_expired: 'This code is not valid or has expired.',
  already_redeemed: 'You have already used this code.',
  code_exhausted: 'This code has reached its usage limit.',
  rpc_error: 'Could not apply the code. Try again later.',
};

// Types for Supabase billing tables
export interface UserBilling {
  id: string;
  user_id: string;
  email?: string;
  current_plan: string;
  credits: number;
  credits_used: number;
  billing_cycle: 'monthly' | 'yearly';
  subscription_status: 'active' | 'inactive' | 'canceled' | 'past_due';
  subscription_id?: string;
  customer_id?: string; // Legacy field - use stripe_customer_id instead
  stripe_customer_id?: string; // Stripe customer ID (cus_xxxxx) for payment processing
  current_period_start?: string;
  current_period_end?: string;
  next_billing_date?: string;
  // Anchor for the 3-day money-back guarantee window. Set when the user
  // upgrades to a paid plan and cleared when they downgrade.
  subscription_started_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  transaction_type: 'consume' | 'refund' | 'grant' | 'purchase';
  credits_amount: number;
  credits_before: number;
  credits_after: number;
  description?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  description?: string;
  credits_per_month?: number | null;
  price_monthly: number;
  price_yearly: number;
  stripe_price_id_monthly?: string;
  stripe_price_id_yearly?: string;
  stripe_product_id?: string;
  features: string[];
  limitations: string[];
  is_popular: boolean;
  is_active: boolean;
}

export interface UserProfile {
  id: string;
  user_id: string;
  email?: string;
  display_name?: string;
  company?: string;
  role?: string;
  bio?: string;
  location?: string;
  website?: string;
  avatar_url?: string;
  created_at?: string;
  updated_at?: string;
  // Allow additional fields from the existing user table
  [key: string]: any;
}

export interface UserMetrics {
  totalAnalyses: number;
  avgScore: number;
  videosAnalyzed: number;
  reportsGenerated: number;
  creditsUsed: number;
  creditsRemaining: number;
  currentPlan: string;
}

// Extended interface for video analysis data storage
export interface VideoAnalysisRecord {
  id: string;
  user_id: string;
  video_name: string;
  analysis_data: Record<string, any>; // JSONB storage for all VideoAnalysisData fields
  analysis_type: 'realtime' | 'batch';
  status: 'completed' | 'processing' | 'failed';
  created_at: string;
  updated_at: string;
  processing_time_seconds?: number;
  file_size_bytes?: number;
  duration_seconds?: number;
  // Searchable metadata fields
  search_tags?: string[];
  platform_fit_summary?: string;
  viral_potential_score?: number;
  completion_rate_pred?: number;
  audience_fit_summary?: string;
  // Aggregated scores for quick queries
  overall_quality_score?: number;
  marketing_effectiveness?: number;
  technical_quality?: number;
}

export interface ContactSalesRequest {
  id: number;
  user_id: string;
  user_name: string;
  company_name: string;
  email: string;
  message: string;
  plan_interested: string;
  submitted_at: string;
  status: 'new' | 'contacted' | 'closed' | 'rejected';
  sales_notes?: string;
  created_at: string;
  updated_at: string;
}

export class BillingService {
  /**
   * Initialize user billing record
   * Handles race conditions gracefully by returning existing record if one exists
   */
  static async initializeUserBilling(userId: string, email?: string): Promise<UserBilling> {
    try {
      const { data, error } = await supabase.rpc('initialize_user_billing', {
        p_user_id: userId,
        p_email: email
      });

      if (error) {
        // Handle duplicate key error gracefully - billing was created in parallel
        const isDuplicateKey = error.code === '23505' || 
                               error.message?.includes('duplicate key') ||
                               error.message?.includes('unique constraint');
        
        if (isDuplicateKey) {
          console.log('ℹ️ Billing record already exists (race condition), fetching existing...');
          // Fetch the existing billing record
          const { data: existingData, error: fetchError } = await supabase
            .from('user_billing')
            .select('*')
            .eq('user_id', userId)
            .single();
          
          if (fetchError) {
            console.error('Error fetching existing billing after duplicate key:', fetchError);
            throw fetchError;
          }
          
          console.log('✅ Retrieved existing billing record');
          return existingData;
        }
        throw error;
      }
      return data;
    } catch (error: any) {
      // Final fallback - try to fetch existing record before giving up
      if (error?.code === '23505' || error?.message?.includes('duplicate key')) {
        try {
          const { data: existingData, error: fetchError } = await supabase
            .from('user_billing')
            .select('*')
            .eq('user_id', userId)
            .single();
          
          if (!fetchError && existingData) {
            console.log('✅ Retrieved existing billing in catch block');
            return existingData;
          }
        } catch (fetchErr) {
          // Ignore fetch error, throw original
        }
      }
      console.error('Error initializing user billing:', error);
      throw error;
    }
  }

  /**
   * Get user billing information
   * Creates a billing record if one doesn't exist
   */
  static async getUserBilling(userId: string): Promise<UserBilling | null> {
    try {
      const { data, error } = await supabase
        .from('user_billing')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No billing record found, create one
          // initializeUserBilling now handles race conditions gracefully
          return await this.initializeUserBilling(userId);
        }
        throw error;
      }

      return data;
    } catch (error: any) {
      // Handle duplicate key errors gracefully - this can happen due to race conditions
      // when another process created the billing while we were trying to
      const isDuplicateKey = error?.code === '23505' || 
                             error?.message?.includes('duplicate key') ||
                             error?.message?.includes('unique constraint');
      
      if (isDuplicateKey) {
        console.log('ℹ️ Race condition detected in getUserBilling, fetching existing record...');
        // The record was created by another process, fetch it
        const { data: existingData, error: fetchError } = await supabase
          .from('user_billing')
          .select('*')
          .eq('user_id', userId)
          .single();
        
        if (!fetchError && existingData) {
          console.log('✅ Retrieved existing billing after race condition');
          return existingData;
        }
      }
      
      console.error('Error getting user billing:', error);
      
      // If Supabase is not configured, return a default billing record
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        return {
          id: userId,
          user_id: userId,
          current_plan: 'free',
          credits: 1, // Default free credits
          credits_used: 0,
          billing_cycle: 'monthly',
          subscription_status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      
      throw error;
    }
  }

  /**
   * Consume credits for a user
   */
  static async consumeCredits(
    userId: string, 
    creditsAmount: number, 
    description: string = 'Video analysis'
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('consume_credits', {
        p_user_id: userId,
        p_credits: creditsAmount,
        p_description: description
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error consuming credits:', error);
      
      // If Supabase is not configured, allow the operation to proceed
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        console.warn('Supabase not configured, allowing credit consumption to proceed');
        return true;
      }
      
      return false;
    }
  }

  /**
   * Grant credits to a user
   */
  static async grantCredits(
    userId: string,
    creditsAmount: number,
    description: string = 'Credit grant'
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('grant_credits', {
        p_user_id: userId,
        p_credits: creditsAmount,
        p_description: description
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error granting credits:', error);
      return false;
    }
  }

  /**
   * Redeem a Supabase-backed promo code (see database/migrations/create_credit_promo_codes.sql).
   */
  static async redeemPromoCode(userId: string, rawCode: string): Promise<RedeemPromoCodeResult> {
    try {
      const { data, error } = await supabase.rpc('redeem_promo_code', {
        p_user_id: userId,
        p_code: rawCode,
      });

      if (error) {
        console.error('redeem_promo_code RPC error:', error);
        return {
          ok: false,
          code: 'rpc_error',
          message: error.message || REDEEM_PROMO_MESSAGES.rpc_error,
        };
      }

      const row = data as {
        success?: boolean;
        error?: string;
        credits_granted?: number;
      } | null;

      if (!row || row.success !== true) {
        const errKey = row?.error || 'rpc_error';
        return {
          ok: false,
          code: errKey,
          message: REDEEM_PROMO_MESSAGES[errKey] || REDEEM_PROMO_MESSAGES.rpc_error,
        };
      }

      const granted = Number(row.credits_granted);
      if (!Number.isFinite(granted) || granted <= 0) {
        return {
          ok: false,
          code: 'rpc_error',
          message: REDEEM_PROMO_MESSAGES.rpc_error,
        };
      }

      return { ok: true, creditsGranted: granted };
    } catch (e: any) {
      console.error('redeemPromoCode failed:', e);
      return {
        ok: false,
        code: 'rpc_error',
        message: e?.message || REDEEM_PROMO_MESSAGES.rpc_error,
      };
    }
  }

  /**
   * Update user subscription plan
   */
  static async updateSubscriptionPlan(
    userId: string,
    planId: string,
    subscriptionData: {
      subscriptionId?: string;
      customerId?: string;
      subscriptionStatus?: 'active' | 'inactive' | 'canceled' | 'past_due';
      billingCycle?: 'monthly' | 'yearly';
      currentPeriodStart?: Date;
      currentPeriodEnd?: Date;
      nextBillingDate?: Date;
      // When the user activated this paid plan. Used to anchor the 3-day
      // refund window. Pass `new Date()` on a successful checkout.
      subscriptionStartedAt?: Date;
    }
  ): Promise<UserBilling> {
    try {
      // Get plan details to set credits
      const plan = getPlanById(planId);
      if (!plan) throw new Error(`Plan ${planId} not found`);

      const updateData: any = {
        current_plan: planId,
        subscription_status: subscriptionData.subscriptionStatus || 'active',
        billing_cycle: subscriptionData.billingCycle || 'monthly',
      };

      // Set credits based on plan
      if (plan.type === 'credits' && plan.credits) {
        updateData.credits = plan.credits;
      } else if (plan.type === 'subscription') {
        // Unlimited plans don't need credit updates
        updateData.credits = 999999; // Large number for unlimited
      }

      // Add subscription details if provided
      if (subscriptionData.subscriptionId) {
        updateData.subscription_id = subscriptionData.subscriptionId;
      }
      if (subscriptionData.customerId) {
        updateData.customer_id = subscriptionData.customerId;
      }
      if (subscriptionData.currentPeriodStart) {
        updateData.current_period_start = subscriptionData.currentPeriodStart.toISOString();
      }
      if (subscriptionData.currentPeriodEnd) {
        updateData.current_period_end = subscriptionData.currentPeriodEnd.toISOString();
      }
      if (subscriptionData.nextBillingDate) {
        updateData.next_billing_date = subscriptionData.nextBillingDate.toISOString();
      }
      if (subscriptionData.subscriptionStartedAt) {
        updateData.subscription_started_at = subscriptionData.subscriptionStartedAt.toISOString();
      }

      const { data, error } = await supabase
        .from('user_billing')
        .update(updateData)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      // Log the plan change
      if (plan.type === 'credits' && plan.credits) {
        await this.logCreditTransaction(userId, {
          transaction_type: 'purchase',
          credits_amount: plan.credits,
          description: `Upgraded to ${plan.name} plan`,
          metadata: { plan_id: planId, subscription_id: subscriptionData.subscriptionId }
        });
      }

      return data;
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      throw error;
    }
  }

  /**
   * Compute the remaining time (in ms) of the 3-day refund window for a
   * given subscription_started_at timestamp. Returns 0 once the window has
   * elapsed and `null` when there's no anchor (i.e. user never had a paid
   * plan, so the question doesn't apply).
   */
  static getRefundWindowRemainingMs(subscriptionStartedAt?: string | null): number | null {
    if (!subscriptionStartedAt) return null;
    const startedAt = new Date(subscriptionStartedAt).getTime();
    if (Number.isNaN(startedAt)) return null;
    const windowMs = REFUND_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return Math.max(0, startedAt + windowMs - Date.now());
  }

  /**
   * Cancel the user's current paid subscription.
   *
   * Behavior:
   * - If we are still within the 3-day money-back window (anchored on
   *   `subscription_started_at`), Stripe is asked to cancel the subscription
   *   AND refund the most recent invoice payment in full.
   * - Outside the window, the subscription is canceled in Stripe with no
   *   refund.
   * - In all cases the local `user_billing` row is downgraded to the free
   *   plan with 0 credits and `subscription_status = 'canceled'` so the UI
   *   reflects the change immediately.
   *
   * Returns whether a refund was issued so callers can show the right toast.
   */
  static async cancelSubscription(userId: string): Promise<{
    success: boolean;
    refunded: boolean;
    refundAmount?: number;
    refundCurrency?: string;
    withinRefundWindow: boolean;
    error?: string;
  }> {
    try {
      const billing = await this.getUserBilling(userId);
      if (!billing) {
        return {
          success: false,
          refunded: false,
          withinRefundWindow: false,
          error: 'Billing record not found',
        };
      }

      const remainingMs = this.getRefundWindowRemainingMs(billing.subscription_started_at);
      const withinRefundWindow = remainingMs !== null && remainingMs > 0;

      // Try to cancel in Stripe. We only call Stripe if we have something to
      // cancel — either a subscription id or a customer id to look it up by.
      let refunded = false;
      let refundAmount: number | undefined;
      let refundCurrency: string | undefined;
      let stripeError: string | undefined;

      if (billing.subscription_id || billing.stripe_customer_id) {
        const result = await cancelStripeSubscription({
          subscriptionId: billing.subscription_id,
          customerId: billing.stripe_customer_id,
          refund: withinRefundWindow,
        });
        if (result.success) {
          refunded = !!result.refunded;
          refundAmount = result.refundAmount;
          refundCurrency = result.refundCurrency;
        } else {
          // Don't bail — we still want to flip the local plan so the user is
          // not left in a half-canceled state. Surface the error to the UI.
          stripeError = result.error;
          console.warn('Stripe cancellation failed, continuing with local downgrade:', result.error);
        }
      }

      // Downgrade locally regardless of Stripe outcome.
      const { error: updateError } = await supabase
        .from('user_billing')
        .update({
          current_plan: 'free',
          credits: 0,
          credits_used: 0,
          subscription_status: 'canceled',
          subscription_id: null,
          subscription_started_at: null,
          current_period_end: null,
          next_billing_date: null,
        })
        .eq('user_id', userId);

      if (updateError) throw updateError;

      // Audit trail — best-effort, swallow failures so the cancel still succeeds.
      try {
        await this.logCreditTransaction(userId, {
          transaction_type: 'refund',
          credits_amount: 0,
          description: refunded
            ? `Subscription canceled within ${REFUND_WINDOW_DAYS}-day money-back window (full refund issued)`
            : 'Subscription canceled',
          metadata: {
            previous_plan: billing.current_plan,
            within_refund_window: withinRefundWindow,
            refund_amount: refundAmount,
            refund_currency: refundCurrency,
            stripe_subscription_id: billing.subscription_id,
            stripe_error: stripeError,
          },
        });
      } catch (logErr) {
        console.warn('Failed to log cancellation transaction:', logErr);
      }

      return {
        success: true,
        refunded,
        refundAmount,
        refundCurrency,
        withinRefundWindow,
        error: stripeError,
      };
    } catch (error: any) {
      console.error('Error canceling subscription:', error);
      return {
        success: false,
        refunded: false,
        withinRefundWindow: false,
        error: error?.message || 'Failed to cancel subscription',
      };
    }
  }

  /**
   * Get user credit transactions
   */
  static async getCreditTransactions(
    userId: string,
    limit: number = 50
  ): Promise<CreditTransaction[]> {
    try {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting credit transactions:', error);
      
      // If Supabase is not configured, return empty array
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        return [];
      }
      
      return [];
    }
  }

  /**
   * Log a credit transaction
   */
  static async logCreditTransaction(
    userId: string,
    transaction: {
      transaction_type: 'consume' | 'refund' | 'grant' | 'purchase';
      credits_amount: number;
      description?: string;
      metadata?: Record<string, any>;
    }
  ): Promise<void> {
    try {
      // Get current billing to calculate before/after
      const billing = await this.getUserBilling(userId);
      if (!billing) throw new Error('User billing not found');

      const { error } = await supabase
        .from('credit_transactions')
        .insert({
          user_id: userId,
          transaction_type: transaction.transaction_type,
          credits_amount: transaction.credits_amount,
          credits_before: billing.credits,
          credits_after: billing.credits + transaction.credits_amount,
          description: transaction.description,
          metadata: transaction.metadata
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error logging credit transaction:', error);
    }
  }

  /**
   * Get all subscription plans
   */
  static async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting subscription plans:', error);
      // Fallback to config plans if database fails
      return PRICING_PLANS.map(plan => ({
        id: plan.id,
        name: plan.name,
        description: plan.description,
        credits_per_month: plan.credits === -1 ? null : plan.credits,
        price_monthly: plan.price.monthly * 100, // Convert to cents
        price_yearly: plan.price.yearly * 100,
        stripe_price_id_monthly: plan.priceId,
        stripe_price_id_yearly: plan.priceId,
        stripe_product_id: plan.productId,
        features: plan.features,
        limitations: plan.limitations,
        is_popular: plan.popular || false,
        is_active: true
      }));
    }
  }

  /**
   * Check if user can run analysis
   */
  static async canRunAnalysis(userId: string): Promise<boolean> {
    try {
      const billing = await this.getUserBilling(userId);
      if (!billing) return false;

      const plan = getPlanById(billing.current_plan);
      if (!plan) return false;

      // Unlimited plans
      if (plan.type === 'subscription' || plan.credits === -1) {
        return billing.subscription_status === 'active';
      }

      // Credit-based plans
      return billing.credits >= CREDIT_CONFIG.costPerRun;
    } catch (error) {
      console.error('Error checking if user can run analysis:', error);
      return false;
    }
  }

  /**
   * Get user profile
   */
  static async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      // First try to get from user_billing table which has user info
      const { data: billingData, error: billingError } = await supabase
        .from('user_billing')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (billingData && !billingError) {
        // Return profile data from user_billing
        return {
          id: billingData.id,
          user_id: billingData.user_id,
          email: billingData.email,
          display_name: billingData.display_name || undefined,
          created_at: billingData.created_at,
          updated_at: billingData.updated_at
        };
      }

      // Fallback: try user_profiles table using correct column name (user_id, not firebase_uid)
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (profileData && !profileError) {
        return {
          id: profileData.id,
          user_id: profileData.user_id,
          email: profileData.email,
          display_name: profileData.display_name,
          company: profileData.company,
          role: profileData.role,
          bio: profileData.bio,
          location: profileData.location,
          website: profileData.website,
          avatar_url: profileData.avatar_url,
          created_at: profileData.created_at,
          updated_at: profileData.updated_at
        };
      }

      // If neither exists, return null
      console.warn('No profile found for user:', userId);
      return null;

    } catch (error) {
      console.error('Error getting user profile:', error);
      
      // If Supabase is not configured, return a default profile
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        return {
          id: userId,
          user_id: userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }
      
      return null;
    }
  }

  /**
   * Create a new user record in the users table
   */
  static async createUser(
    userId: string,
    userData: {
      email?: string;
      display_name?: string;
      avatar_url?: string;
    }
  ): Promise<UserProfile | null> {
    try {
      // Insert into user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          user_id: userId,
          email: userData.email,
          display_name: userData.display_name,
          avatar_url: userData.avatar_url,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating user:', error);
        throw error;
      }

      return data ? {
        id: data.id,
        user_id: data.user_id,
        email: data.email,
        display_name: data.display_name,
        avatar_url: data.avatar_url,
        created_at: data.created_at,
        updated_at: data.updated_at
      } : null;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Save/update user profile
   */
  static async saveUserProfile(
    userId: string,
    profile: {
      display_name?: string;
      company?: string;
      role?: string;
      bio?: string;
      location?: string;
      website?: string;
    }
  ): Promise<UserProfile | null> {
    try {
      // First, try to update user_billing table with display_name
      if (profile.display_name) {
        await supabase
          .from('user_billing')
          .update({ display_name: profile.display_name })
          .eq('user_id', userId);
      }

      // Then upsert to user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: userId,
          display_name: profile.display_name,
          company: profile.company,
          role: profile.role,
          bio: profile.bio,
          location: profile.location,
          website: profile.website,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving user profile:', error);
        throw error;
      }

      return data ? {
        id: data.id,
        user_id: data.user_id,
        email: data.email,
        display_name: data.display_name,
        company: data.company,
        role: data.role,
        bio: data.bio,
        location: data.location,
        website: data.website,
        avatar_url: data.avatar_url,
        created_at: data.created_at,
        updated_at: data.updated_at
      } : null;
    } catch (error) {
      console.error('Error saving user profile:', error);
      throw error;
    }
  }

  /**
   * Get user metrics and usage statistics
   */
  static async getUserMetrics(userId: string): Promise<UserMetrics> {
    try {
      const billing = await this.getUserBilling(userId);

      const creditsUsed = billing?.credits_used || 0;
      const creditsRemaining = billing?.credits || 0;
      const currentPlan = billing?.current_plan || 'free';

      // Real counts from video_analysis_runs (same source the dashboard uses).
      // Forensic runs are excluded so the numbers match the dashboard.
      const { data: runs, error: runsError } = await supabase
        .from('video_analysis_runs')
        .select('status')
        .eq('firebase_uid', userId)
        .neq('video_source_type', 'forensic');

      if (runsError) {
        console.error('Error fetching analysis runs for metrics:', runsError);
      }

      const totalAnalyses = runs?.length || 0;
      const completedCount = (runs || []).filter(
        (r: { status: string }) => r.status === 'completed'
      ).length;

      // Real avg score from video_analysis_storage.searchable_data
      // (JSONB key overall_quality_score, 0..1). The UI shows it as `x.x/10`.
      let avgScore = 0;
      if (completedCount > 0) {
        const { data: scoreRows, error: scoreError } = await supabase
          .from('video_analysis_storage')
          .select('searchable_data')
          .eq('user_id', userId)
          .eq('status', 'completed');

        if (scoreError) {
          console.error('Error fetching analysis scores for metrics:', scoreError);
        } else if (scoreRows && scoreRows.length > 0) {
          avgScore = averageOverallQualityScoreFromStorageRows(scoreRows);
        }
      }

      return {
        totalAnalyses,
        avgScore,
        videosAnalyzed: completedCount,
        reportsGenerated: completedCount,
        creditsUsed,
        creditsRemaining,
        currentPlan,
      };
    } catch (error) {
      console.error('Error getting user metrics:', error);
      return {
        totalAnalyses: 0,
        avgScore: 0,
        videosAnalyzed: 0,
        reportsGenerated: 0,
        creditsUsed: 0,
        creditsRemaining: 0,
        currentPlan: 'free'
      };
    }
  }

  /**
   * Get recent credit transactions for billing overview
   */
  static async getRecentUsage(userId: string, limit: number = 10): Promise<CreditTransaction[]> {
    try {
      return await this.getCreditTransactions(userId, limit);
    } catch (error) {
      console.error('Error getting recent usage:', error);
      return [];
    }
  }

  /**
   * Store video analysis data in Supabase
   */
  static async storeVideoAnalysis(
    userId: string,
    videoName: string,
    analysisData: Record<string, any>,
    options: {
      analysisType?: 'realtime' | 'batch';
      processingTimeSeconds?: number;
      fileSizeBytes?: number;
      durationSeconds?: number;
    } = {}
  ): Promise<string> {
    try {
      // Generate unique analysis ID
      const analysisId = `analysis_${userId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Extract key metrics for searchable fields
      const searchableData = {
        search_tags: this.extractSearchTags(analysisData),
        platform_fit_summary: this.extractPlatformFitSummary(analysisData),
        viral_potential_score: analysisData?.predictions?.viral_potential_score,
        completion_rate_pred: analysisData?.predictions?.completion_rate_pred,
        audience_fit_summary: this.extractAudienceFitSummary(analysisData),
        overall_quality_score: this.calculateOverallQualityScore(analysisData),
        marketing_effectiveness: this.calculateMarketingEffectiveness(analysisData),
        technical_quality: this.calculateTechnicalQuality(analysisData),
      };

      // Store in video_analysis_storage table via RPC
      try {
        const { error } = await supabase.rpc('store_video_analysis', {
          p_user_id: userId,
          p_video_name: videoName,
          p_analysis_id: analysisId,
          p_analysis_data: analysisData,
          p_analysis_type: options.analysisType || 'realtime',
          p_processing_time_seconds: options.processingTimeSeconds,
          p_file_size_bytes: options.fileSizeBytes,
          p_duration_seconds: options.durationSeconds,
          p_searchable_data: searchableData
        });

        if (error) {
          // Handle specific error types more gracefully
          if (error.code === 'PGRST202' || error.message?.includes('function') || error.message?.includes('does not exist')) {
            console.warn(`⚠️ Supabase RPC function store_video_analysis not available, data will be available from API`);
          } else {
            console.warn(`⚠️ Could not store ${videoName} in Supabase:`, error.message, 'data will be available from API');
          }
          return analysisId;
        }

        const dashboardMetrics = buildDashboardMetricsForDb(analysisData);
        try {
          const { data: runRows } = await supabase
            .from('video_analysis_runs')
            .select('analysis_metadata')
            .eq('firebase_uid', userId)
            .eq('video_name', videoName)
            .eq('status', 'completed')
            .order('created_at', { ascending: false })
            .limit(1);

          const existingMetadata =
            runRows?.[0]?.analysis_metadata && typeof runRows[0].analysis_metadata === 'object'
              ? runRows[0].analysis_metadata
              : {};

          await supabase
            .from('video_analysis_runs')
            .update({
              analysis_metadata: {
                ...existingMetadata,
                ...dashboardMetrics,
              },
            })
            .eq('firebase_uid', userId)
            .eq('video_name', videoName)
            .eq('status', 'completed');
        } catch (metadataError) {
          console.warn(`⚠️ Could not persist dashboard metrics for ${videoName}:`, metadataError);
        }
      } catch (storeError: any) {
        // Handle network and other errors gracefully
        console.warn(`⚠️ Error storing ${videoName} in Supabase:`, storeError.message || storeError, 'data will be available from API');
      }
      return analysisId;
    } catch (error) {
      console.error('Error storing video analysis:', error);

      // If Supabase is not configured, just return a mock ID
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        console.warn('Supabase not configured, returning mock analysis ID');
        return `mock_analysis_${Date.now()}`;
      }

      throw error;
    }
  }

  /**
   * Retrieve video analysis data from Supabase
   */
  static async getVideoAnalysis(
    userId: string,
    videoName: string,
    analysisId?: string
  ): Promise<VideoAnalysisRecord | null> {
    try {
      // Set a shorter timeout for the RPC call
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      const { data, error } = await supabase.rpc('get_video_analysis', {
        p_user_id: userId,
        p_video_name: videoName,
        p_analysis_id: analysisId
      }, {
        signal: controller.signal
      } as any);

      clearTimeout(timeoutId);

      if (error) {
        // Handle specific error codes
        if (error.code === '57014' || error.message?.includes('statement timeout')) {
          console.warn(`⚠️ Supabase RPC timeout for ${videoName}, will use API fallback`);
          return null; // Return null to trigger API fallback
        }
        
        // Handle 500 Internal Server Error (function doesn't exist or has errors)
        if (error.code === 'PGRST202' || error.message?.includes('function') || error.message?.includes('does not exist')) {
          console.warn(`⚠️ Supabase RPC function not available for ${videoName}, will use API fallback`);
          return null;
        }
        
        // Handle other database errors gracefully
        console.warn(`⚠️ Supabase RPC error for ${videoName}:`, error.message, 'will use API fallback');
        return null; // Return null to trigger API fallback instead of throwing
      }
      
      return data;
    } catch (error: any) {
      // Handle network and other errors gracefully
      console.warn(`⚠️ Error getting video analysis for ${videoName}:`, error.message || error, 'will use API fallback');

      // Handle timeout errors by returning null (will trigger API fallback)
      if (error.name === 'AbortError' || error.code === '57014' || error.message?.includes('timeout')) {
        console.warn(`⚠️ Timeout getting ${videoName} from Supabase, will use API fallback`);
        return null;
      }

      // If Supabase is not configured, return null
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        return null;
      }

      // For all other errors, return null to allow API fallback
      return null;
    }
  }

  /**
   * Search video analyses based on criteria
   */
  static async searchVideoAnalyses(
    userId: string,
    criteria: {
      searchText?: string;
      minViralScore?: number;
      platform?: string;
      dateFrom?: string;
      dateTo?: string;
      limit?: number;
    } = {}
  ): Promise<VideoAnalysisRecord[]> {
    try {
      const { data, error } = await supabase.rpc('search_video_analyses', {
        p_user_id: userId,
        p_search_text: criteria.searchText || '',
        p_min_viral_score: criteria.minViralScore || 0,
        p_platform: criteria.platform || '',
        p_date_from: criteria.dateFrom,
        p_date_to: criteria.dateTo,
        p_limit: criteria.limit || 50
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching video analyses:', error);

      // If Supabase is not configured, return empty array
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        return [];
      }

      return [];
    }
  }

  /**
   * Get analysis statistics for dashboard
   */
  static async getAnalysisStats(userId: string): Promise<{
    totalAnalyses: number;
    avgViralScore: number;
    avgQualityScore: number;
    recentAnalyses: VideoAnalysisRecord[];
  }> {
    try {
      const { data, error } = await supabase.rpc('get_analysis_stats', {
        p_user_id: userId
      });

      if (error) throw error;

      // Get recent analyses separately since SQL function returns empty array for now
      const recentAnalyses = await this.searchVideoAnalyses(userId, { limit: 5 });

      return {
        totalAnalyses: data.total_analyses,
        avgViralScore: data.avg_viral_score,
        avgQualityScore: data.avg_quality_score,
        recentAnalyses
      };
    } catch (error) {
      console.error('Error getting analysis stats:', error);

      // If Supabase is not configured, return mock data
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        return {
          totalAnalyses: 0,
          avgViralScore: 0,
          avgQualityScore: 0,
          recentAnalyses: []
        };
      }

      throw error;
    }
  }

  /**
   * Extract searchable tags from analysis data
   */
  private static extractSearchTags(analysisData: Record<string, any>): string[] {
    const tags: string[] = [];

    // Add category-based tags
    if (analysisData?.file_source?.orientation) {
      tags.push(analysisData.file_source.orientation.toLowerCase());
    }
    if (analysisData?.file_source?.color_space) {
      tags.push(analysisData.file_source.color_space.toLowerCase());
    }

    // Add content-based tags
    if (analysisData?.nlp?.topics_primary) {
      tags.push(analysisData.nlp.topics_primary.toLowerCase());
    }
    if (analysisData?.nlp?.topics_secondary) {
      tags.push(...analysisData.nlp.topics_secondary.map((t: string) => t.toLowerCase()));
    }

    // Add platform recommendations
    if (analysisData?.platform_fit?.best_platforms) {
      tags.push(...analysisData.platform_fit.best_platforms.map((p: string) => p.toLowerCase()));
    }

    return Array.from(new Set(tags)); // Remove duplicates
  }

  /**
   * Extract platform fit summary
   */
  private static extractPlatformFitSummary(analysisData: Record<string, any>): string {
    const platforms = analysisData?.platform_fit?.best_platforms || [];
    const warnings = analysisData?.platform_fit?.platform_specific_warnings || [];

    return `Best platforms: ${platforms.join(', ')}. Warnings: ${warnings.join(', ')}.`;
  }

  /**
   * Extract audience fit summary
   */
  private static extractAudienceFitSummary(analysisData: Record<string, any>): string {
    const personas = analysisData?.personas || {};
    const demographics = analysisData?.Human_Demographics || {};

    return `Personas: ${personas.influencer_archetype || 'General'}. Demographics: ${demographics.age_range || 'Unknown'}.`;
  }

  /**
   * Calculate overall quality score
   */
  private static calculateOverallQualityScore(analysisData: Record<string, any>): number {
    const visualQuality = analysisData?.visual_quality?.visual_clarity_index || 0;
    const audioQuality = analysisData?.audio?.loudness_integrated_lufs || -23; // Reference level
    const technicalQuality = analysisData?.file_source?.fps || 0;

    // Normalize and weight scores
    const normalizedVisual = Math.min(visualQuality / 10, 1);
    const normalizedAudio = Math.max(0, Math.min(1, (audioQuality + 40) / 40)); // -40 to 0 dB range
    const normalizedTechnical = Math.min(technicalQuality / 60, 1); // Assume 60fps is max

    return (normalizedVisual * 0.4 + normalizedAudio * 0.3 + normalizedTechnical * 0.3);
  }

  /**
   * Calculate marketing effectiveness score
   */
  private static calculateMarketingEffectiveness(analysisData: Record<string, any>): number {
    const viralPotential = analysisData?.predictions?.viral_potential_score || 0;
    const messageClarity = analysisData?.marketing?.message_clarity || 0;
    const ctaPresence = analysisData?.ocr_text?.cta_text_detected ? 1 : 0;

    return (viralPotential * 0.4 + messageClarity / 10 * 0.4 + ctaPresence * 0.2);
  }

  /**
   * Calculate technical quality score
   */
  private static calculateTechnicalQuality(analysisData: Record<string, any>): number {
    const visualQuality = analysisData?.visual_quality?.visual_clarity_index || 0;
    const audioQuality = analysisData?.audio?.loudness_integrated_lufs || -23;
    const encodingQuality = analysisData?.encoding?.video_bitrate_kbps || 0;

    const normalizedVisual = Math.min(visualQuality / 10, 1);
    const normalizedAudio = Math.max(0, Math.min(1, (audioQuality + 40) / 40));
    const normalizedEncoding = Math.min(encodingQuality / 5000, 1); // 5Mbps reference

    return (normalizedVisual * 0.4 + normalizedAudio * 0.3 + normalizedEncoding * 0.3);
  }

  /**
   * Store contact sales request
   */
  static async storeContactSalesRequest(
    contactData: {
      user_id: string;
      user_name: string;
      company_name: string;
      email: string;
      message: string;
      plan_interested: string;
    }
  ): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('store_contact_sales_request', {
        p_user_id: contactData.user_id,
        p_user_name: contactData.user_name,
        p_company_name: contactData.company_name,
        p_email: contactData.email,
        p_message: contactData.message,
        p_plan_interested: contactData.plan_interested
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error storing contact sales request:', error);

      // If Supabase is not configured, just return a mock ID
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        console.warn('Supabase not configured, returning mock contact request ID');
        return Math.floor(Math.random() * 1000000);
      }

      throw error;
    }
  }

  /**
   * Get contact sales requests (for admin purposes)
   */
  static async getContactSalesRequests(
    limit: number = 50,
    offset: number = 0,
    status: string = 'new'
  ): Promise<ContactSalesRequest[]> {
    try {
      const { data, error } = await supabase.rpc('get_contact_sales_requests', {
        p_limit: limit,
        p_offset: offset,
        p_status: status
      });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting contact sales requests:', error);

      // If Supabase is not configured, return empty array
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        return [];
      }

      return [];
    }
  }

  /**
   * Update contact sales request status
   */
  static async updateContactSalesStatus(
    id: number,
    status: string,
    salesNotes?: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('update_contact_sales_status', {
        p_id: id,
        p_status: status,
        p_sales_notes: salesNotes
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating contact sales status:', error);

      // If Supabase is not configured, return false
      if (error instanceof Error && error.message && error.message.includes('Supabase not configured')) {
        return false;
      }

      return false;
    }
  }

  /**
   * Process Stripe webhook (for future implementation)
   */
  static async processStripeWebhook(
    eventType: string,
    eventData: any,
    stripeEventId: string
  ): Promise<void> {
    try {
      // Check if event already processed
      const { data: existingEvent } = await supabase
        .from('billing_events')
        .select('id')
        .eq('stripe_event_id', stripeEventId)
        .single();

      if (existingEvent) {
        // Webhook event already processed
        return;
      }

      // Log the event
      const { error: logError } = await supabase
        .from('billing_events')
        .insert({
          event_type: eventType,
          stripe_event_id: stripeEventId,
          event_data: eventData,
          user_id: eventData.customer?.metadata?.user_id
        });

      if (logError) throw logError;

      // Process different event types
      switch (eventType) {
        case 'customer.subscription.created':
        case 'customer.subscription.updated':
          await this.handleSubscriptionUpdate(eventData);
          break;
        case 'customer.subscription.deleted':
          await this.handleSubscriptionCancellation(eventData);
          break;
        case 'invoice.payment_succeeded':
          await this.handlePaymentSuccess(eventData);
          break;
        case 'invoice.payment_failed':
          await this.handlePaymentFailed(eventData);
          break;
      }

      // Mark as processed
      await supabase
        .from('billing_events')
        .update({ processed: true, processed_at: new Date().toISOString() })
        .eq('stripe_event_id', stripeEventId);

    } catch (error) {
      console.error('Error processing webhook:', error);
      throw error;
    }
  }

  private static async handleSubscriptionUpdate(subscriptionData: any): Promise<void> {
    // Implementation for subscription updates
  }

  private static async handleSubscriptionCancellation(subscriptionData: any): Promise<void> {
    // Implementation for subscription cancellations
  }

  private static async handlePaymentSuccess(invoiceData: any): Promise<void> {
    // Implementation for successful payments
  }

  private static async handlePaymentFailed(invoiceData: any): Promise<void> {
    // Implementation for failed payments
  }

  // ===========================
  // Video Agent Methods
  // ===========================

  /**
   * Get Video Agent usage stats for the current month
   * Tracks usage via credit_transactions table
   */
  static async getVideoAgentUsage(userId: string): Promise<{
    chatQueriesUsed: number;
    searchQueriesUsed: number;
  }> {
    try {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      // Query credit_transactions for Video Agent usage this month
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('description, credits_amount')
        .eq('user_id', userId)
        .gte('created_at', startOfMonth.toISOString())
        .or('description.ilike.%Video Agent chat%,description.ilike.%Video Agent search%');

      if (error) throw error;

      const usage = {
        chatQueriesUsed: 0,
        searchQueriesUsed: 0,
      };

      if (data) {
        data.forEach((transaction: any) => {
          if (transaction.description?.includes('Video Agent chat')) {
            usage.chatQueriesUsed++;
          } else if (transaction.description?.includes('Video Agent search')) {
            usage.searchQueriesUsed++;
          }
        });
      }

      return usage;
    } catch (error) {
      console.error('Error getting Video Agent usage:', error);
      return { chatQueriesUsed: 0, searchQueriesUsed: 0 };
    }
  }

  /**
   * Check if user can perform Video Agent operation
   */
  static async canUseVideoAgent(
    userId: string,
    operation: 'chat' | 'search' | 'create_index'
  ): Promise<boolean> {
    try {
      const billing = await this.getUserBilling(userId);
      if (!billing) return false;

      const plan = getPlanById(billing.current_plan);
      if (!plan) return false;

      // Check if user is paid
      const isPaid = await this.isPaidUser(userId);

      // Create index requires >100 credits
      if (operation === 'create_index') {
        return billing.credits > CREDIT_CONFIG.videoAgent.unlimitedThreshold;
      }

      // Paid users: check credit balance (1 credit per chat)
      if (isPaid && operation === 'chat') {
        return billing.credits >= 1;
      }

      // Paid users: check credit balance for search (if we want to charge for search too)
      if (isPaid && operation === 'search') {
        return billing.credits >= 1;
      }

      // Free users: check monthly limits
      if (!isPaid) {
      const usage = await this.getVideoAgentUsage(userId);
      
      if (operation === 'chat') {
        return usage.chatQueriesUsed < CREDIT_CONFIG.freeTierLimits.chatQueries;
      }
      
      if (operation === 'search') {
        return usage.searchQueriesUsed < CREDIT_CONFIG.freeTierLimits.indexSearches;
        }
      }

      return false;
    } catch (error) {
      console.error('Error checking Video Agent access:', error);
      return false;
    }
  }

  /**
   * Check if user has unlimited Video Agent access
   */
  static async isVideoAgentUnlimited(userId: string): Promise<boolean> {
    try {
      const billing = await this.getUserBilling(userId);
      if (!billing) return false;

      const plan = getPlanById(billing.current_plan);
      if (!plan) return false;

      return (
        billing.credits > CREDIT_CONFIG.videoAgent.unlimitedThreshold ||
        (billing.subscription_status === 'active' && plan.type === 'subscription')
      );
    } catch (error) {
      console.error('Error checking Video Agent unlimited access:', error);
      return false;
    }
  }

  /**
   * Check if user is a paid user (current_plan is not 'free')
   */
  static async isPaidUser(userId: string): Promise<boolean> {
    try {
      const billing = await this.getUserBilling(userId);
      if (!billing) return false;

      // User is paid if their current plan is not 'free'
      return billing.current_plan !== 'free';
    } catch (error) {
      console.error('Error checking if user is paid:', error);
      return false;
    }
  }

  /**
   * Consume credits for Video Agent chat query
   * Paid users: charge 1 credit per prompt
   * Free users: log transaction for tracking (0 credits deducted)
   */
  static async consumeVideoAgentChat(userId: string): Promise<boolean> {
    try {
      const isPaid = await this.isPaidUser(userId);
      
      if (isPaid) {
        // Paid users: charge 1 credit per prompt
        const success = await this.consumeCredits(userId, 1, 'Video Agent chat query');
        if (!success) {
          console.error('Failed to consume credits for Video Agent chat');
          return false;
        }
        return true;
      }

      // Free users: log transaction for tracking purposes (0 credits deducted)
      // Free users are limited by monthly query count, not credit balance
      await this.logCreditTransaction(userId, {
        transaction_type: 'consume',
        credits_amount: 0,
        description: 'Video Agent chat query',
        metadata: { 
          operation: 'chat',
          timestamp: new Date().toISOString()
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error consuming Video Agent chat:', error);
      return false;
    }
  }

  /**
   * Consume credits for Video Agent search query
   * Note: Free users are limited by monthly query count, not credits.
   * This logs the transaction for tracking without deducting credits.
   */
  static async consumeVideoAgentSearch(userId: string): Promise<boolean> {
    try {
      const isUnlimited = await this.isVideoAgentUnlimited(userId);
      
      // Unlimited users don't need tracking
      if (isUnlimited) return true;

      // Log transaction for tracking purposes (0 credits deducted)
      // Free users are limited by monthly query count, not credit balance
      await this.logCreditTransaction(userId, {
        transaction_type: 'consume',
        credits_amount: 0,
        description: 'Video Agent index search',
        metadata: { 
          operation: 'search',
          timestamp: new Date().toISOString()
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error logging Video Agent search usage:', error);
      // Don't block the operation if logging fails
      return true;
    }
  }

  /**
   * Check if user has a Stripe customer ID
   * Returns the Stripe customer ID if exists, null if not
   */
  static async getStripeCustomerId(userId: string): Promise<string | null> {
    try {
      const billing = await this.getUserBilling(userId);
      return billing?.stripe_customer_id || null;
    } catch (error) {
      console.error('Error checking Stripe customer ID:', error);
      return null;
    }
  }

  /**
   * Check if user has a Stripe customer and return status
   */
  static async hasStripeCustomer(userId: string): Promise<boolean> {
    const customerId = await this.getStripeCustomerId(userId);
    return !!customerId;
  }

  /**
   * Update the email in user_billing record
   * This is used to backfill emails for users who were created without one
   */
  static async updateBillingEmail(userId: string, email: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_billing')
        .update({ 
          email,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) {
        console.error('Error updating billing email:', error);
        return false;
      }

      console.log('✅ Billing email updated for user:', userId);
      return true;
    } catch (error) {
      console.error('Error updating billing email:', error);
      return false;
    }
  }

  /**
   * Backfill/create Stripe customer for a user who doesn't have one
   * This is useful for fixing users who were created before Stripe integration
   * or whose Stripe customer creation failed during signup
   * 
   * @param userId Firebase user ID
   * @param email User email (optional, will be fetched from billing if not provided)
   * @param name User display name (optional)
   * @returns Promise with success status and customer ID if created
   */
  static async backfillStripeCustomer(
    userId: string,
    email?: string,
    name?: string
  ): Promise<{
    success: boolean;
    customerId?: string;
    existingCustomer?: boolean;
    error?: string;
  }> {
    try {
      console.log('🔍 Checking if user needs Stripe customer backfill:', userId);

      // Check if user already has a Stripe customer ID
      const existingCustomerId = await this.getStripeCustomerId(userId);
      if (existingCustomerId) {
        console.log('✅ User already has Stripe customer:', existingCustomerId);
        return {
          success: true,
          customerId: existingCustomerId,
          existingCustomer: true
        };
      }

      // Get user billing data to get email if not provided
      const billing = await this.getUserBilling(userId);
      if (!billing) {
        return {
          success: false,
          error: 'User billing record not found. Please initialize billing first.'
        };
      }

      const userEmail = email || billing.email;
      if (!userEmail) {
        return {
          success: false,
          error: 'User email is required but not found in billing record'
        };
      }

      console.log('💳 Creating/finding Stripe customer for user:', {
        userId,
        email: userEmail,
        name
      });

      // Find or create Stripe customer
      const stripeResult = await findOrCreateStripeCustomer({
        email: userEmail,
        name: name,
        firebaseUid: userId,
        metadata: {
          firebase_uid: userId,
          created_via: 'backfill_utility',
          backfilled_at: new Date().toISOString()
        }
      });

      if (!stripeResult.success || !stripeResult.customer) {
        return {
          success: false,
          error: stripeResult.error || 'Failed to create Stripe customer'
        };
      }

      const customerId = stripeResult.customer.customerId;
      console.log('✅ Stripe customer found/created:', customerId);

      // Save Stripe customer ID to user_billing
      const { error: updateError } = await supabase
        .from('user_billing')
        .update({
          stripe_customer_id: customerId,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (updateError) {
        // Check if this is a stripe_customer_id unique constraint violation
        // This happens when another user already has this Stripe customer ID (same email, different Firebase UID)
        const isStripeCustomerIdConstraint = updateError.message?.includes('user_billing_stripe_customer_id_key');
        
        if (isStripeCustomerIdConstraint) {
          // This is actually OK - another user with the same email already has this Stripe customer
          // We can either skip saving or try to update without the stripe_customer_id
          console.log('ℹ️ Stripe customer ID already exists for another user (same email), treating as success');
          return {
            success: true,
            customerId,
            existingCustomer: true
          };
        }
        
        // Try upsert as fallback for other errors
        console.warn('⚠️ Update failed, trying upsert as fallback:', updateError.message);
        const { error: upsertError } = await supabase
          .from('user_billing')
          .upsert({
            user_id: userId,
            email: userEmail,
            stripe_customer_id: customerId,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id'
          });

        if (upsertError) {
          // Check again for stripe_customer_id constraint
          const isStripeConstraintOnUpsert = upsertError.message?.includes('user_billing_stripe_customer_id_key');
          
          if (isStripeConstraintOnUpsert) {
            console.log('ℹ️ Stripe customer ID already exists (upsert), treating as success');
            return {
              success: true,
              customerId,
              existingCustomer: true
            };
          }
          
          console.error('❌ CRITICAL: Failed to save Stripe customer ID to user_billing:', {
            error: upsertError.message,
            code: upsertError.code,
            customerId
          });
          return {
            success: false,
            error: `Stripe customer created but failed to save to database: ${upsertError.message}`
          };
        }
      }

      console.log('✅ Stripe customer ID saved to user_billing:', {
        userId,
        customerId,
        existingCustomer: stripeResult.existingCustomer
      });

      return {
        success: true,
        customerId,
        existingCustomer: stripeResult.existingCustomer || false
      };

    } catch (error: any) {
      console.error('❌ Error backfilling Stripe customer:', error);
      return {
        success: false,
        error: error.message || 'Failed to backfill Stripe customer'
      };
    }
  }
}
